import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import { AlertCircle, Bookmark, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND =
  process.env.REACT_APP_BACKEND_URL || "https://jobzam-backend.onrender.com";

function formatSalary(min, max) {
  if (!min && !max) return "Salary not listed";
  if (min && max) return `£${min.toLocaleString()} - £${max.toLocaleString()}`;
  if (min) return `From £${min.toLocaleString()}`;
  return `Up to £${max.toLocaleString()}`;
}

export default function SavedJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [unsavingId, setUnsavingId] = useState(null);

  const getSessionOrThrow = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) throw new Error("Please login to view saved jobs");
    return session;
  };

  const fetchSaved = async () => {
    try {
      setErr("");
      setLoading(true);

      const session = await getSessionOrThrow();

      const res = await fetch(`${BACKEND}/jobs/saved`, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `Failed to load saved jobs (${res.status})${text ? `: ${text}` : ""}`
        );
      }

      const data = await res.json();

      // Support BOTH shapes:
      // 1) { jobs: [...] }
      // 2) [...]
      const list = Array.isArray(data) ? data : data.jobs || [];
      setJobs(list);
    } catch (e) {
      setErr(e.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const unsaveJob = async (jobId) => {
    try {
      setUnsavingId(jobId);

      const session = await getSessionOrThrow();

      const res = await fetch(`${BACKEND}/jobs/action`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ job_id: jobId, saved: false }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `Failed to unsave job (${res.status})${text ? `: ${text}` : ""}`
        );
      }

      // remove immediately from UI (fast)
      setJobs((prev) => prev.filter((j) => j.id !== jobId));
    } catch (e) {
      alert(e.message || "Failed to unsave job");
    } finally {
      setUnsavingId(null);
    }
  };

  useEffect(() => {
    fetchSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div role="status" className="flex min-h-52 flex-col items-center justify-center rounded-3xl bg-slate-50 text-center text-slate-600"><Loader2 className="mb-3 h-7 w-7 animate-spin text-blue-600" aria-hidden="true" /><p className="font-semibold">Loading saved jobs...</p></div>;
  if (err) return <div role="alert" className="rounded-3xl border border-rose-200 bg-rose-50 p-7 text-center"><AlertCircle className="mx-auto h-7 w-7 text-rose-600" aria-hidden="true" /><p className="mt-3 font-semibold text-rose-800">We couldn’t load your saved jobs.</p><button type="button" onClick={fetchSaved} className="mt-4 rounded-xl bg-white px-4 py-2 text-sm font-bold text-rose-700 shadow-sm ring-1 ring-rose-200">Try again</button></div>;

  if (!jobs.length) {
    return <div className="rounded-3xl border border-slate-200 bg-slate-50 p-8 text-center"><Bookmark className="mx-auto h-8 w-8 text-blue-600" aria-hidden="true" /><h2 className="mt-4 text-xl font-bold text-slate-950">No saved jobs yet</h2><p className="mt-2 text-slate-600">You haven’t saved any jobs yet.</p><Link to="/jobs" className="mt-5 inline-flex min-h-11 items-center justify-center rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white">Explore jobs</Link></div>;
  }

  return (
    <div className="grid gap-4">
      <div><h2 className="text-2xl font-black text-slate-950">Saved Jobs</h2><p className="mt-1 text-sm text-slate-600">{jobs.length} saved job{jobs.length === 1 ? "" : "s"}</p></div>
      {jobs.map((job) => (
        <div
          key={job.id}
          className="border border-slate-200 rounded-2xl p-5 hover:shadow-md transition bg-white"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-slate-900">
                {job.title}
              </h3>

              <p className="text-sm text-slate-600 mt-1">
                <span className="font-medium">{job.company}</span> • {job.location} •{" "}
                {job.country}
              </p>

              <p className="text-sm text-slate-600 mt-1">
                {formatSalary(job.salary_min, job.salary_max)}
              </p>
            </div>

            <div className="flex flex-col items-end gap-2 shrink-0">
              <a
                href={job.job_url || "#"}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
              >
                Open →
              </a>

              <button
                onClick={() => unsaveJob(job.id)}
                disabled={unsavingId === job.id}
                className={
                  "px-3 py-1 rounded-md text-xs transition " +
                  (unsavingId === job.id
                    ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200")
                }
              >
                {unsavingId === job.id ? "..." : "Unsave"}
              </button>
            </div>
          </div>

          {job.description?.trim() ? (
            <p className="text-sm text-slate-700 mt-3 line-clamp-3">
              {job.description}
            </p>
          ) : null}

          <div className="mt-3 text-xs text-slate-500 flex items-center justify-between">
            <span>Saved</span>

            {job._action?.applied ? (
              <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                Applied
              </span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}
